import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet, RefreshCw } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useApiMessages } from "../../hooks/useApiMessages";
import { useVisitorScope } from "../../components/visitors/useVisitorScope";
import { PageHeader, ScopeFilters, StatusBadge } from "../../components/visitors/VisitorShared";
import { VisitDetailDialog } from "../../components/visitors/VisitDetailDialog";
import {
  dayRangeIso,
  exportToExcel,
  exportToPdf,
  fmtDateTime,
  fmtDuration,
  todayInput,
  visitExportColumns,
  visitorApi,
} from "../../components/visitors/visitorUtils";

const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("en-CA");
};

const GroupTable = ({ title, rows }) => {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-500">—</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("visitors.common.name")}</TableHead>
                <TableHead className="text-right">{t("visitors.reports.total")}</TableHead>
                <TableHead className="text-right">{t("visitors.reports.entries")}</TableHead>
                <TableHead className="text-right">{t("visitors.reports.exits")}</TableHead>
                <TableHead className="text-right">{t("visitors.reports.denied")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.slice(0, 15).map((r) => (
                <TableRow key={r.name}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-right">{r.total}</TableCell>
                  <TableCell className="text-right">{r.entries}</TableCell>
                  <TableCell className="text-right">{r.exits}</TableCell>
                  <TableCell className="text-right">{r.denied}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default function VisitReports() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const scope = useVisitorScope();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayInput());
  const [report, setReport] = useState(null);
  const [qrLog, setQrLog] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [tab, setTab] = useState("summary");
  const [detailId, setDetailId] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "summary") setReport(await visitorApi.get("/reports", { ...scope.params, ...dayRangeIso(from, to) }));
      if (tab === "qr") setQrLog(await visitorApi.get("/qr/validations", { ...scope.params, limit: 500 }));
      if (tab === "audit") setAuditLog(await visitorApi.get("/audit", { company_id: scope.companyId, limit: 500 }));
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.loadError"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.companyId, scope.facilityId, from, to, tab]);

  useEffect(() => {
    load();
  }, [load]);

  const s = report?.summary;
  const summaryRows = s
    ? [
        [t("visitors.reports.total"), s.total],
        [t("visitors.reports.entries"), s.entries],
        [t("visitors.reports.exits"), s.exits],
        [t("visitors.reports.inside"), s.inside],
        [t("visitors.reports.denied"), s.denied],
        [t("visitors.reports.uniqueVisitors"), s.unique_visitors],
        [t("visitors.reports.withVehicle"), s.with_vehicle],
        [t("visitors.reports.avgDuration"), fmtDuration(s.avg_duration_minutes)],
        [t("visitors.reports.rejectedPreauths"), s.rejected_preauths],
        [t("visitors.reports.pendingPreauths"), s.pending_preauths],
        [t("visitors.reports.incidents"), s.incidents],
      ]
    : [];

  const facilityName = scope.facility?.name || t("visitors.common.allFacilities");
  const title = `${t("visitors.reports.title")} · ${facilityName}`;
  const subtitle = `${from} – ${to}`;
  const filename = `reporte_visitas_${from}_${to}`;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("visitors.reports.title")}
        subtitle={t("visitors.reports.subtitle")}
        actions={
          <>
            <Button variant="outline" size="icon" onClick={load} aria-label={t("visitors.common.refresh")}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              variant="outline"
              disabled={!report?.visits?.length || tab !== "summary"}
              onClick={() => exportToExcel(report.visits, visitExportColumns(t), filename, `${title} (${subtitle})`)}
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button
              variant="outline"
              disabled={!report || tab !== "summary"}
              onClick={() =>
                exportToPdf({ title, subtitle, summary: summaryRows, columns: visitExportColumns(t), rows: report.visits, filename })
              }
            >
              <FileDown className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </>
        }
      />

      <ScopeFilters scope={scope}>
        <div>
          <Label className="text-sm text-slate-600 mb-1 block">{t("visitors.common.dateFrom")}</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <Label className="text-sm text-slate-600 mb-1 block">{t("visitors.common.dateTo")}</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </ScopeFilters>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="summary">{t("visitors.reports.summary")}</TabsTrigger>
          <TabsTrigger value="qr">{t("visitors.reports.qrLog")}</TabsTrigger>
          <TabsTrigger value="audit">{t("visitors.reports.auditLog")}</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="space-y-6 mt-4">
          {s && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {summaryRows.map(([label, value]) => (
                <Card key={label}>
                  <CardContent className="p-3">
                    <p className="text-xl font-bold">{value ?? "—"}</p>
                    <p className="text-xs text-slate-500">{label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {report && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <GroupTable title={t("visitors.reports.byFacility")} rows={report.by_facility} />
                <GroupTable title={t("visitors.reports.byHost")} rows={report.by_host} />
                <GroupTable title={t("visitors.reports.byGuard")} rows={report.by_guard} />
                <GroupTable title={t("visitors.reports.byShift")} rows={report.by_workday} />
              </div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    {t("visitors.reports.visitsTable")} ({report.visits.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-2">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("visitors.common.visitor")}</TableHead>
                        <TableHead className="hidden md:table-cell">{t("visitors.common.facility")}</TableHead>
                        <TableHead className="hidden md:table-cell">{t("visitors.common.host")}</TableHead>
                        <TableHead>{t("visitors.common.checkIn")}</TableHead>
                        <TableHead className="hidden sm:table-cell">{t("visitors.common.duration")}</TableHead>
                        <TableHead>{t("visitors.common.status")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.visits.slice(0, 300).map((v) => (
                        <TableRow key={v.id} className="cursor-pointer" onClick={() => setDetailId(v.id)}>
                          <TableCell className="font-medium">{v.visitor_name}</TableCell>
                          <TableCell className="hidden md:table-cell">{v.facility_name}</TableCell>
                          <TableCell className="hidden md:table-cell">{v.host_name || "—"}</TableCell>
                          <TableCell className="text-sm">{fmtDateTime(v.check_in_at || v.created_at)}</TableCell>
                          <TableCell className="hidden sm:table-cell">{fmtDuration(v.duration_minutes)}</TableCell>
                          <TableCell>
                            <StatusBadge status={v.status} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {report.visits.length > 300 && (
                    <p className="text-xs text-slate-500 p-3">{t("visitors.reports.truncated")}</p>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="qr" className="mt-4">
          <Card>
            <CardContent className="p-0 sm:p-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("visitors.common.date")}</TableHead>
                    <TableHead>{t("visitors.reports.result")}</TableHead>
                    <TableHead>{t("visitors.common.visitor")}</TableHead>
                    <TableHead className="hidden sm:table-cell">{t("visitors.reports.user")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {qrLog.map((q) => (
                    <TableRow key={q.id}>
                      <TableCell className="text-sm">{fmtDateTime(q.timestamp)}</TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={q.result === "valid" || q.result === "used_for_check_in" ? "border-green-300 text-green-700" : "border-red-300 text-red-700"}
                        >
                          {t(`visitors.qrReasons.${q.result}`)}
                        </Badge>
                      </TableCell>
                      <TableCell>{q.visitor_name || "—"}</TableCell>
                      <TableCell className="hidden sm:table-cell">{q.user_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <Card>
            <CardContent className="p-0 sm:p-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("visitors.common.date")}</TableHead>
                    <TableHead>{t("visitors.reports.action")}</TableHead>
                    <TableHead className="hidden md:table-cell">{t("visitors.common.details")}</TableHead>
                    <TableHead>{t("visitors.reports.user")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLog.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="text-sm whitespace-nowrap">{fmtDateTime(a.timestamp)}</TableCell>
                      <TableCell>{t(`visitors.auditActions.${a.action}`, a.action)}</TableCell>
                      <TableCell className="hidden md:table-cell text-xs text-slate-600 max-w-md truncate">
                        {Object.entries(a.details || {})
                          .filter(([, v]) => v !== null && typeof v !== "object")
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(" · ")}
                      </TableCell>
                      <TableCell className="text-sm">{a.user_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <VisitDetailDialog visitId={detailId} open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)} scope={scope} />
    </div>
  );
}
